import { isAfter, isBefore, isSameDay, parseISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { ShiftProviderTravelEnum, CommandIndexProvidersQuery, ShiftStatusEnum, CalendarBlockEventStatusEnum } from "~/graphql/__generated__/graphql";
import { LocalCalendarBlock, LocalShift } from "~/hooks/visitDragDropReducer";
import { OrgConfig, VisitResourceRequirement } from "~/providers/OrgConfigProvider";
import { compact, startCase } from "lodash";
import { CalendarBlockConflict, calendarBlockIsFilter, isAllowedInBlock } from "@caredna-marlin/utils/calendarBlockHelper";
import { Addressable } from "@caredna-marlin/utils/addresses";
import { hasOverlap } from "@caredna-marlin/utils/client";

export enum ValidationErrorType {
  NO_PROVIDER_SHIFT = 'NO_PROVIDER_SHIFT',
  OUTSIDE_SHIFT_HOURS = 'OUTSIDE_SHIFT_HOURS',
  DRIVE_TIME_OUTSIDE_SHIFT = 'DRIVE_TIME_OUTSIDE_SHIFT',
  PROVIDER_TYPE_MISMATCH = 'PROVIDER_TYPE_MISMATCH',
  TRAVEL_TYPE_MISMATCH = 'TRAVEL_TYPE_MISMATCH',
  SHIFT_NOT_FOUND = 'SHIFT_NOT_FOUND',
  WRONG_FILTER_BLOCK_VISIT_TYPE = 'WRONG_FILTER_BLOCK_VISIT_TYPE',
  WRONG_FILTER_BLOCK_ADDRESS = 'WRONG_FILTER_BLOCK_ADDRESS',
  CARE_TEAM_MISSING = 'CARE_TEAM_MISSING',
  INACTIVE_PROVIDER = 'INACTIVE_PROVIDER',
  PROVIDER_NOT_LICENSED = 'PROVIDER_NOT_LICENSED',
  UNASSIGNED_PROVIDER = 'UNASSIGNED_PROVIDER',
  SERVICE_LINE_MISMATCH = 'SERVICE_LINE_MISMATCH',
}

export interface ValidationError {
  type: ValidationErrorType;
  message: string;
  shouldAllowDrop: boolean;
  severity?: number;
}

/**
 * Validates provider shift existence and time constraints
 */
export function validateProviderShift(
  providerId: number,
  localShifts: LocalShift[],
  eventStart: Date,
  eventEnd: Date
): ValidationError[] {
  const providerShifts = localShifts.filter((shift) => shift.provider?.id === providerId && shift.status === ShiftStatusEnum.Confirmed);

  // Check if provider has any shifts
  if (!providerShifts.length) {
    return [{
      type: ValidationErrorType.NO_PROVIDER_SHIFT,
      message: 'Provider has no shift scheduled',
      shouldAllowDrop: false,
    }];
  }

  // Check if event time is within any shift hours
  const validShift = providerShifts.find((shift) => {
    const shiftStart = parseISO(shift.startTime);
    const shiftEnd = parseISO(shift.endTime);
    return !isBefore(eventStart, shiftStart) && !isAfter(eventEnd, shiftEnd);
  });

  if (!validShift) {
    return [{
      type: ValidationErrorType.OUTSIDE_SHIFT_HOURS,
      message: 'Visit time is outside of provider\'s shift hours',  
      shouldAllowDrop: false,
    }];
  }

  return [];
}

/**
 * Validates that drive time buffers don't extend outside shift boundaries
 * Returns warnings (not blocking errors) when buffers exceed shift edges
 * Respects ignoreDriveTimeShiftStart/ignoreDriveTimeShiftEnd config settings
 */
export function validateDriveTimeBuffers(
  eventStart: Date,
  eventEnd: Date,
  bufferBeforeMinutes: number,
  bufferAfterMinutes: number,
  shiftStart: Date,
  shiftEnd: Date,
  config: { ignoreDriveTimeShiftStart: boolean; ignoreDriveTimeShiftEnd: boolean }
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Check if buffer before extends past shift start
  if (!config.ignoreDriveTimeShiftStart && bufferBeforeMinutes > 0) {
    const eventStartWithBuffer = new Date(eventStart.getTime() - bufferBeforeMinutes * 60000);
    if (eventStartWithBuffer < shiftStart) {
      errors.push({
        type: ValidationErrorType.DRIVE_TIME_OUTSIDE_SHIFT,
        message: 'Drive time extends before shift start',
        shouldAllowDrop: true, // Warning only, allow drop
      });
    }
  }

  // Check if buffer after extends past shift end
  if (!config.ignoreDriveTimeShiftEnd && bufferAfterMinutes > 0) {
    const eventEndWithBuffer = new Date(eventEnd.getTime() + bufferAfterMinutes * 60000);
    if (eventEndWithBuffer > shiftEnd) {
      errors.push({
        type: ValidationErrorType.DRIVE_TIME_OUTSIDE_SHIFT,
        message: 'Drive time extends after shift end',
        shouldAllowDrop: true, // Warning only, allow drop
      });
    }
  }

  return errors;
}

const unknownReason = (reason: never): never => {
  throw new Error(`Unknown reason: ${(reason as any)}`);
}

export function validateFilterBlocks(
  providerId: number,
  visitTypeId: number,
  address: Addressable | null,
  localCalendarBlocks: LocalCalendarBlock[],
  startTime: Date,
  endTime: Date,
) {

  // This should never happen, but the GQL types technically allow it, and it's more convenient
  // to check against it here than to either try and get GQL to agree that required relations are
  // in fact required or to bail early. :shrug:
  if (!address) {
    address = {
      addressStreet: '',
      addressCity: '',
      addressState: '',
      addressZip: '',
    };
  }

  const disallowReason = localCalendarBlocks
    // Only calendar blocks for this provider
    .filter(cb => cb.providerId === providerId && calendarBlockIsFilter(cb))

    // Only scheduled calendar blocks 
    . filter(cb => CalendarBlockEventStatusEnum.Canceled !== cb.eventStatus)

    // Turn start/end times into Dates rather than strings
    .map(cb => ({...cb, startTime: new Date(cb.startTime), endTime: new Date(cb.endTime)}))

    // Only blocks that overlap with the proposed time
    .filter(block => hasOverlap(block, { startTime, endTime }))

    // Turn the calendar blocks to reason enums
    .map(block => isAllowedInBlock(visitTypeId, address, block))

    // And find the first reason to block the drop (if any)
    .find(reason => reason !== CalendarBlockConflict.ALLOWED);

  switch (disallowReason) {
    case CalendarBlockConflict.WRONG_VISIT_TYPE: return [{
      type: ValidationErrorType.WRONG_FILTER_BLOCK_VISIT_TYPE,
      message: 'Provider has time block for specific visit types',
      shouldAllowDrop: false,
    }]

    case CalendarBlockConflict.WRONG_ADDRESS: return [{
      type: ValidationErrorType.WRONG_FILTER_BLOCK_ADDRESS,
      message: 'Provider has a facility visit at a different address',
      shouldAllowDrop: false,
    }]

    case CalendarBlockConflict.NO_VISITS_ALLOWED:
    case undefined: return [];

    // Type guard; because of the `never`, ts will make sure the above cases are exhaustive
    default: return unknownReason(disallowReason);
  }
}

/**
 * Validates travel type compatibility between visit requirements and provider shift
 */
export function validateTravelTypeCompatibility(
  inHome: boolean | undefined,
  providerTravel?: ShiftProviderTravelEnum | null
): ValidationError[] {
  if (inHome === true && providerTravel === ShiftProviderTravelEnum.VirtualOnly) {
    return [{
      type: ValidationErrorType.TRAVEL_TYPE_MISMATCH,
      message: 'In-home visit cannot be scheduled on a virtual shift',
      shouldAllowDrop: false,
    }];
  }

  if (inHome === false && providerTravel === ShiftProviderTravelEnum.InHomeOnly) {
    return [{
      type: ValidationErrorType.TRAVEL_TYPE_MISMATCH,
      message: 'Virtual visit cannot be scheduled on an in-home shift',
      shouldAllowDrop: false,
    }];
  }

  return [];
}

/**
 * Validates provider type compatibility with resource requirements
 */
export function validateProviderTypeCompatibility(
  providerType: { id: number } | null,
  allowedVisitResourceRequirements: VisitResourceRequirement[],
): ValidationError[] {
  if (!providerType) {
    return [{
      type: ValidationErrorType.PROVIDER_TYPE_MISMATCH,
      message: 'Provider type not found',
      shouldAllowDrop: false,
    }];
  }

  const allowed = allowedVisitResourceRequirements.some((vrr) => vrr.providerTypes?.some((pt) => pt.id === providerType.id));
  
  if (!allowed) {
    const validTypes = allowedVisitResourceRequirements.map((vrr) => vrr.providerTypes?.map((pt) => startCase(`${pt.name}`)).join(', ')).join(', ');
    return [{
      type: ValidationErrorType.PROVIDER_TYPE_MISMATCH,
      message: `Visit requires a ${validTypes?.length ? validTypes : ' valid provider type'}`,
      shouldAllowDrop: false,
    }];
  }

  return [];
}

/**
 * Gets provider type from provider shift and org config
 */
export function getProviderType(
  providerShift: { provider?: { providerTypeId?: number | null } | null } | null,
  orgConfig: OrgConfig
): { id: number; name: string } | null {
  if (!providerShift) return null;
  if (!providerShift.provider?.providerTypeId) return null;
  return orgConfig.providerTypes[providerShift.provider.providerTypeId];
}

export const validateContinuityOfCare = (
  providerShift: { 
    provider?: { id?: number | null, firstName?: string | null, lastName?: string | null, providerTypeId?: number | null } | null;
    providerTravel?: ShiftProviderTravelEnum | null;
  } | null,
  careTeamProviderIds: number[],
  eventType: "visit_request" | "visit_resource" | "calendar_block",
  isMultiResource: boolean
): ValidationError[] => {

  if (!careTeamProviderIds?.length) {
    return [];
  }

  const providerNotOnCareTeam = providerShift?.provider?.id && !careTeamProviderIds.includes(providerShift?.provider?.id);
  const shouldShowCareTeamWarning = providerNotOnCareTeam && (eventType === "visit_resource" || (eventType === "visit_request" && !isMultiResource));
  
  if (shouldShowCareTeamWarning) {
    return [{
      type: ValidationErrorType.CARE_TEAM_MISSING,
      message: `Provider ${providerShift?.provider?.firstName} ${providerShift?.provider?.lastName} is not part of the patient's care team`,
      shouldAllowDrop: true,
    }];
  }

  return [];
}

/**
 * Provider trait interface for license checking
 */
export interface ProviderTrait {
  trait: string;
  value: string;
}

/**
 * Checks if a provider has a state license for the given state
 */
export const hasStateLicense = (traits: ProviderTrait[] | undefined | null, state: string | null | undefined): boolean => {
  if (!traits || !state) return false;
  return traits.some((trait) => trait.trait === 'stateLicense' && trait.value === state);
}

/**
 * Validates that a provider is assigned to the correct service line for the visit
 */
export const validateProviderServiceLine = (
  provider: { 
    id?: number | null;
    firstName?: string | null;
    lastName?: string | null;
    allowedServiceLineIds?: number[] | null;
  },
  serviceLines: { id: number; name?: string | null }[]
): ValidationError[] => {
  if (!serviceLines.length) {
    return [];
  }

  const isAssignedToAnyServiceLine = serviceLines.some((serviceLine) =>
    provider.allowedServiceLineIds?.includes(serviceLine.id)
  );

  if (!isAssignedToAnyServiceLine) {
    const serviceLineNames = compact(serviceLines
      .map((serviceLine) => serviceLine.name)
    ).join(' or ');
    const serviceLineDisplayName = serviceLineNames
      ? `the ${serviceLineNames} service line${serviceLines.length > 1 ? 's' : ''}`
      : 'this service line';
    return [{
      type: ValidationErrorType.SERVICE_LINE_MISMATCH,
      message: `${provider.firstName} ${provider.lastName} is not assigned to ${serviceLineDisplayName}`,
      shouldAllowDrop: false,
      severity: 3,
    }];
  }
  return [];
};

/**
 * Validates provider licensing for the patient's state when the provider type requires it.
 * This checks if any of the allowed provider types for the visit require a license,
 * and if so, validates that the provider has the appropriate state license.
 */
export const validateProviderLicense = (
  provider: { 
    id?: number | null;
    firstName?: string | null;
    lastName?: string | null;
    providerTypeId?: number | null;
    traits?: ProviderTrait[] | null;
  } | null | undefined,
  patientState: string | null | undefined,
  orgConfig: OrgConfig,
  allowedVisitResourceRequirements: VisitResourceRequirement[],
): ValidationError[] => {
  // If no provider or no patient state, skip validation
  if (!provider || !patientState) {
    return [];
  }

  // Get the provider type
  const providerType = provider.providerTypeId ? orgConfig.providerTypes[provider.providerTypeId] : null;
  
  // If no provider type found, skip validation
  if (!providerType) {
    return [];
  }

  // Check if this provider type requires a state license
  // The requireStateLicense flag is on the provider type
  const requiresLicense = (providerType as { requireStateLicense?: boolean })?.requireStateLicense;
  
  // If this provider type doesn't require a license check, skip validation
  if (!requiresLicense) {
    return [];
  }

  // Check if the provider type is allowed for this visit
  const isProviderTypeAllowed = allowedVisitResourceRequirements.some(vrr => 
    vrr.providerTypes?.some(pt => pt.id === providerType.id)
  );

  // If the provider type isn't even allowed for this visit, skip license validation
  // (the provider type mismatch error will be shown instead)
  if (!isProviderTypeAllowed) {
    return [];
  }

  // Check if provider has a license for the patient's state
  if (!hasStateLicense(provider.traits, patientState)) {
    return [{
      type: ValidationErrorType.PROVIDER_NOT_LICENSED,
      message: `Provider ${provider.firstName} ${provider.lastName} is not licensed in ${patientState}`,
      shouldAllowDrop: false, // Block the drop - provider must be licensed
    }];
  }

  return [];
}

/**
 * Validates business rules like provider type matching and travel type compatibility
 */
export function validateBusinessRules(
  providerShift: { 
    provider?: { providerTypeId?: number | null } | null;
    providerTravel?: ShiftProviderTravelEnum | null;
  } | null,
  orgConfig: OrgConfig,
  allowedVisitResourceRequirements: VisitResourceRequirement[],
  inHome: boolean,
  eventType: "visit_request" | "visit_resource" | "calendar_block",
  isMultiResource: boolean,
  careTeamProviderIds?: number[],
): ValidationError[] {

  if (!providerShift) {
    return [{
      type: ValidationErrorType.SHIFT_NOT_FOUND,
      message: 'Provider shift not found',
      shouldAllowDrop: false,
    }];
  }

  const providerType = getProviderType(providerShift, orgConfig);

  const errors: ValidationError[] = [
    ...validateProviderTypeCompatibility(providerType, allowedVisitResourceRequirements),
    ...validateTravelTypeCompatibility(inHome, providerShift.providerTravel),
  ];

  const blockingErrors = errors.filter((error) => !error.shouldAllowDrop);
  // if there are no blocking errors, then we can validate continuity of care
  if (blockingErrors.length === 0) {
    errors.push(...validateContinuityOfCare(providerShift, careTeamProviderIds ?? [], eventType, isMultiResource));
  }

  return errors;
}

/**
 * Provider data interface for select components
 */
export interface SelectProviderData {
  _vrrId: number;
  resourceId: string;
  offset: number;
  duration: number;
}

export const defaultProviderData: SelectProviderData = {
  _vrrId: 0,
  resourceId: '',
  offset: 0,
  duration: 0
};

const normalizeDatesForComparison = (
  dateStr: string,
  targetTimezone?: string | null
): Date => {
  const date = new Date(dateStr);
  if (targetTimezone) {
    return toZonedTime(date, targetTimezone);
  }
  
  return date;
};

export const getProviderShiftsInSameDay = (
  providerId: number,
  shifts: CommandIndexProvidersQuery["shift"],
  startTime: string,
  timezone?: string | null
): Exclude<CommandIndexProvidersQuery["shift"], undefined> => {
  const visitStart = normalizeDatesForComparison(startTime, timezone);
  return (shifts || []).filter(shift => {
    const shiftStartTime = normalizeDatesForComparison(shift.startTime, timezone);
    
    return shift.providerId === providerId && 
           isSameDay(shiftStartTime, visitStart) && 
           shift.status === ShiftStatusEnum.Confirmed;
  });
};

// DEPRECATED: use getProviderShiftsInSameDay for new code
export const getProviderShiftInSameDay = (
  providerId: number,
  shifts: CommandIndexProvidersQuery["shift"],
  startTime: string,
  timezone?: string | null
) => {
  const visitStart = normalizeDatesForComparison(startTime, timezone);
  return shifts?.find(shift => {
    const shiftStartTime = normalizeDatesForComparison(shift.startTime, timezone);
    
    return shift.providerId === providerId && 
           isSameDay(shiftStartTime, visitStart) && 
           shift.status === ShiftStatusEnum.Confirmed;
  });
};

export const getProviderShiftsInDateRange = (
  providerId: number,
  shifts: CommandIndexProvidersQuery["shift"],
  startTime: string,
  endTime: string,
  timezone?: string | null
): Exclude<CommandIndexProvidersQuery["shift"], undefined> => {
  const visitStart = normalizeDatesForComparison(startTime, timezone);
  const visitEnd = normalizeDatesForComparison(endTime, timezone);
  return (shifts || []).filter(shift => {
    const shiftStartTime = normalizeDatesForComparison(shift.startTime, timezone);
    const shiftEndTime = normalizeDatesForComparison(shift.endTime, timezone);

    /* 
      @TODO: to consider change shiftStartTime <= visitStart && shiftEndTime >= visitEnd to capture partial overlaps, eg: 
      this would capture partial overlaps, eg:
      Shift: 9-5
      Visit: 4:30-5:30
      I believe we use this in the service-side logic, which is why visits hanging off the end of shifts still get drive times.
    */

    return shift.providerId === providerId && 
           shiftStartTime <= visitStart && 
           shiftEndTime >= visitEnd && 
           shift.status === ShiftStatusEnum.Confirmed;
  });
};

// DEPRECATED: use getProviderShiftsInDateRange for new code
export const getProviderShiftInDateRange = (
  providerId: number,
  shifts: CommandIndexProvidersQuery["shift"],
  startTime: string,
  endTime: string,
  timezone?: string | null
) => {
  const visitStart = normalizeDatesForComparison(startTime, timezone);
  const visitEnd = normalizeDatesForComparison(endTime, timezone);
  return shifts?.find(shift => {
    const shiftStartTime = normalizeDatesForComparison(shift.startTime, timezone);
    const shiftEndTime = normalizeDatesForComparison(shift.endTime, timezone);

    /* 
      @TODO: to consider change shiftStartTime <= visitStart && shiftEndTime >= visitEnd to capture partial overlaps, eg: 
      this would capture partial overlaps, eg:
      Shift: 9-5
      Visit: 4:30-5:30
      I believe we use this in the service-side logic, which is why visits hanging off the end of shifts still get drive times.
    */

    return shift.providerId === providerId && 
           shiftStartTime <= visitStart && 
           shiftEndTime >= visitEnd && 
           shift.status === ShiftStatusEnum.Confirmed;
  });
};

export const isRequirementEligibleForShift = (
  requirement: VisitResourceRequirement,
  providerShift: { providerTravel: ShiftProviderTravelEnum }
) => {
  return providerShift &&
    (!requirement.inHome && providerShift.providerTravel === ShiftProviderTravelEnum.VirtualOnly)
    || (requirement.inHome && providerShift.providerTravel === ShiftProviderTravelEnum.InHomeOnly)
    || (providerShift.providerTravel === ShiftProviderTravelEnum.Either);
}


/**
 * Get assignable providers for a specific visit resource requirement
 */
export const getAssignableProvidersForRequirement = (
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  shifts: CommandIndexProvidersQuery["shift"],
  requirement: VisitResourceRequirement,
  startTime: string,
  endTime: string,
  visitTimezone?: string | null,
  strictShiftFiltering?: boolean
): CommandIndexProvidersQuery["commandIndexProviders"]["providers"] => {
  const ptIds =requirement.providerTypes.map(pt => pt.id) || [];

  // Filter by provider type
  let filteredProviders = providers.filter(p => 
    p.providerType && ptIds.includes(p.providerType.id) && p.active
  );

  // Filter by shift availability and travel type compatibility
  if (requirement.providerTypes.length || requirement.inHome !== undefined) {
    filteredProviders = filteredProviders.filter(provider => {
      if (!startTime || !endTime) return false;
      const providerShifts = strictShiftFiltering ?
        getProviderShiftsInDateRange(provider.id, shifts, startTime, endTime, visitTimezone) :
        getProviderShiftsInSameDay(provider.id, shifts, startTime, visitTimezone);
    
      const hasShifts = providerShifts.length > 0

      if (strictShiftFiltering && !hasShifts) return false;

      // Check travel type compatibility
      const inHome = requirement.inHome;
      if (hasShifts && inHome && providerShifts.every(s => s.providerTravel === ShiftProviderTravelEnum.VirtualOnly)) {
        return false;
      }

      if (hasShifts && inHome === false && providerShifts.every(s => s.providerTravel === ShiftProviderTravelEnum.InHomeOnly)) {
        return false;
      }

      return true;
    });
  }

  return filteredProviders;
};
